import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BRAND_OPERATOR_LINE, COMPANY } from "@/lib/companyInfo";

const About = () => {
  useSEO({
    title: "About Female Partner Calculator | Evidence-Based Dating Statistics",
    description:
      "Female Partner Calculator uses real US Census, CDC, and Pew Research data to calculate dating compatibility probabilities. Learn about the project, our principles, and the team behind the tool.",
    keywords:
      "about female partner calculator, dating statistics tool, evidence-based dating, demographic probability calculator, Summit Lane Digital",
    canonical: "https://femalepartnercalculator.com/about",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Female Partner Calculator",
      description:
        "Evidence-based dating probability calculators powered by real US Census, CDC, and Pew Research data.",
      url: "https://femalepartnercalculator.com/about",
      mainEntity: {
        "@type": "Organization",
        name: COMPANY.brandName,
        url: COMPANY.website,
        parentOrganization: {
          "@type": "Organization",
          name: COMPANY.legalName,
        },
        email: COMPANY.emails.support,
      },
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              About Female Partner Calculator
            </h1>
            <p className="text-xl text-muted-foreground">
              Evidence-based dating probability insights, powered by real
              demographic data
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>What This Is</CardTitle>
              <CardDescription>
                A statistical tool — not relationship advice
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="leading-relaxed">
                Female Partner Calculator is a free web-based statistical tool
                that calculates the probability of finding a compatible partner
                in the United States based on specific demographic and
                behavioral criteria. Every number comes from authoritative
                public sources — the U.S. Census Bureau, CDC surveys, and Pew
                Research Center studies.
              </p>
              <p className="leading-relaxed">
                The results are{" "}
                <strong>population-level probabilities</strong>, not personal
                predictions. They show how common or uncommon a combination of
                traits is across the U.S. adult population, giving users a
                data-grounded perspective on the dating market.
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  <strong>Disclaimer:</strong> This tool provides statistical
                  analysis only. It is not relationship advice, matchmaking, or
                  professional counseling. Individual dating outcomes depend on
                  far more than demographics.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Who We Are</CardTitle>
              <CardDescription>
                The organization behind the project
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="leading-relaxed">
                {BRAND_OPERATOR_LINE} We build data-driven tools that make
                publicly available demographic statistics accessible and
                actionable for everyday decision-making.
              </p>
              <p className="leading-relaxed">
                Our team combines expertise in statistics, software engineering,
                and data visualization to turn dense government datasets into
                clear, interactive experiences that anyone can use.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>What We Offer</CardTitle>
              <CardDescription>
                Calculators, research, and state-level data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Calculators</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link
                      to="/"
                      className="text-primary hover:underline"
                    >
                      Female Partner Calculator
                    </Link>{" "}
                    — probability of finding a compatible woman based on age,
                    race, politics, body type, and more.
                  </li>
                  <li>
                    <Link
                      to="/male-partner-calculator"
                      className="text-primary hover:underline"
                    >
                      Male Partner Calculator
                    </Link>{" "}
                    — the same approach applied to the male dating pool:
                    height, income, education, and beyond.
                  </li>
                  <li>
                    <Link
                      to="/ideal-woman-calculator"
                      className="text-primary hover:underline"
                    >
                      Ideal Woman Calculator
                    </Link>{" "}
                    — filter by age, height, body type, income, and education
                    to see what percentage of US women match.
                  </li>
                  <li>
                    <Link
                      to="/dating-standards-calculator"
                      className="text-primary hover:underline"
                    >
                      Dating Standards Calculator
                    </Link>{" "}
                    — test how realistic your combined dating standards are
                    against real data.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Research & Data</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link
                      to="/stats"
                      className="text-primary hover:underline"
                    >
                      US Dating Statistics
                    </Link>{" "}
                    — research articles on marriage rates, income, education,
                    height, and body type.
                  </li>
                  <li>
                    <Link
                      to="/states"
                      className="text-primary hover:underline"
                    >
                      Statistics by State
                    </Link>{" "}
                    — dating pool estimates for all 50 states and DC.
                  </li>
                  <li>
                    <Link
                      to="/methodology"
                      className="text-primary hover:underline"
                    >
                      Statistical Methodology
                    </Link>{" "}
                    — how the multiplicative probability model works.
                  </li>
                  <li>
                    <Link
                      to="/data-sources"
                      className="text-primary hover:underline"
                    >
                      Data Sources
                    </Link>{" "}
                    — every statistic traced back to its source.
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Our Principles</CardTitle>
              <CardDescription>
                What guides every calculation and design decision
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold mb-1">Transparency</h4>
                  <p className="text-sm text-muted-foreground">
                    Every data point is cited. The full methodology is published.
                    Users can verify any number we show.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold mb-1">Statistical Objectivity</h4>
                  <p className="text-sm text-muted-foreground">
                    We report what government data says, without editorializing
                    the results. The math is the same regardless of which
                    criteria a user selects.
                  </p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold mb-1">Citable Sources Only</h4>
                  <p className="text-sm text-muted-foreground">
                    No made-up numbers. Every statistic traces to the U.S.
                    Census Bureau, CDC NHANES, CDC NSFG, or Pew Research Center
                    publications.
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold mb-1">Clear Disclaimers</h4>
                  <p className="text-sm text-muted-foreground">
                    We are explicit about what the tool is and is not. Results
                    are statistical probabilities — not dating advice, life
                    guidance, or value judgments.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact</CardTitle>
              <CardDescription>
                Questions, feedback, or partnership inquiries
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">General Support</h4>
                  <p className="text-sm text-muted-foreground">
                    Questions about the calculator, methodology, or your results.
                  </p>
                  <a
                    href={`mailto:${COMPANY.emails.support}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {COMPANY.emails.support}
                  </a>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Business & Partnerships</h4>
                  <p className="text-sm text-muted-foreground">
                    Media inquiries, data licensing, or collaboration proposals.
                  </p>
                  <a
                    href={`mailto:${COMPANY.emails.business}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {COMPANY.emails.business}
                  </a>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Data & Methodology</h4>
                  <p className="text-sm text-muted-foreground">
                    Questions about our data sources or statistical approach.
                  </p>
                  <a
                    href={`mailto:${COMPANY.emails.data}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {COMPANY.emails.data}
                  </a>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Privacy & Legal</h4>
                  <p className="text-sm text-muted-foreground">
                    Data privacy requests or legal correspondence.
                  </p>
                  <a
                    href={`mailto:${COMPANY.emails.privacy}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {COMPANY.emails.privacy}
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <p className="text-muted-foreground">
              Ready to try it?{" "}
              <Link to="/" className="text-primary hover:underline">
                Open the calculator
              </Link>{" "}
              or learn more about our{" "}
              <Link to="/methodology" className="text-primary hover:underline">
                methodology
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default About;
